import React from "react";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useSettings } from "../hooks/useSettings";

const HomeAboutPreview = () => {
    const { t } = useTranslation();
    const { theme } = useSettings();

    const timelineItems = [
        {
            id: 1,
            label: "#1",
            title: t("about.timeline.sop", "Statement of Purpose (SoP)"),
            description: t(
                "about.timeline.sopDesc",
                "We refine your story and ambitions into a concise statement that stands out."
            ),
            color: "#f6c343",
        },
        {
            id: 2,
            label: "#2",
            title: t("about.timeline.recommendation", "Recommendation Letter"),
            description: t(
                "about.timeline.recommendationDesc",
                "Guidance to secure impactful references aligned with your goals."
            ),
            color: "#ff6b6b",
        },
        {
            id: 3,
            label: "#3",
            title: t("about.timeline.admission", "Admission Process"),
            description: t(
                "about.timeline.admissionDesc",
                "A step-by-step path from shortlist to submission with zero guesswork."
            ),
            color: "#15cda8",
        },
        {
            id: 4,
            label: "#4",
            title: t("about.timeline.motivation", "Motivation Letter"),
            description: t(
                "about.timeline.motivationDesc",
                "Craft persuasive motivation letters tailored to each program."
            ),
            color: "#36c5f0",
        },
        {
            id: 5,
            label: "#5",
            title: t("about.timeline.consultation", "Consultation"),
            description: t(
                "about.timeline.consultationDesc",
                "Personalized sessions with our mentors to sharpen your application strategy."
            ),
            color: "#7c3aed",
        },
        {
            id: 6,
            label: "#6",
            title: t("about.timeline.cover", "Cover Letter"),
            description: t(
                "about.timeline.coverDesc",
                "Concise cover letters that highlight your fit and readiness."
            ),
            color: "#0f172a",
        },
        {
            id: 7,
            label: "#7",
            title: t("about.timeline.studyPlan", "Study Plan"),
            description: t(
                "about.timeline.studyPlanDesc",
                "A pragmatic study roadmap that aligns courses, research, and outcomes."
            ),
            color: "#1ec2a3",
        },
        {
            id: 8,
            label: "#8",
            title: t("about.timeline.editing", "Editing"),
            description: t(
                "about.timeline.editingDesc",
                "Sharp, error-free drafts with cohesive narratives and tone."
            ),
            color: "#ef4444",
        },
        {
            id: 9,
            label: "#9",
            title: t("about.timeline.cv", "CV"),
            description: t(
                "about.timeline.cvDesc",
                "Structured CVs that foreground achievements, impact, and potential."
            ),
            color: "#10b981",
        },
    ];

    const backgroundImage = theme?.coverImages?.about;

    return (
        <section
            id="about"
            className="relative overflow-hidden py-20 md:py-28 bg-slate-50 text-slate-900"
        >
            {backgroundImage && (
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `linear-gradient(135deg, rgba(249,250,251,0.92), rgba(244,246,248,0.94)), url(${backgroundImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
            )}

            <div className="relative max-w-6xl mx-auto px-6">
                <div className="flex items-start flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                    <div className="space-y-6 max-w-3xl">
                        <p className="text-sm uppercase tracking-[0.3em] text-safir-secondary">
                            {t("about.label", "About Us")}
                        </p>
                        <h2 className="text-3xl md:text-4xl font-bold">
                            {t("about.title", "Safir the Elite")}
                        </h2>
                        <p className="text-slate-600">
                            {t(
                                "about.subtitle",
                                "We guide you through every milestone of your academic journey with structured, human-first coaching."
                            )}
                        </p>
                    </div>
                    <a
                        href="/about"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white shadow-sm border border-slate-200 hover:-translate-y-0.5 hover:shadow-md transition"
                    >
                        <span className="font-semibold">
                            {t("about.learnMore", "About page")}
                        </span>
                        <FaArrowRight size={14} />
                    </a>
                </div>

                <div className="relative mt-12 md:mt-16">
                    <div className="absolute left-1/2 top-0 bottom-0 w-[2px]  -translate-x-1/2 bg-gradient-to-b from-safir-secondary/20 via-slate-300 to-safir-secondary/20" />

                    <div className="absolute left-1/2 -translate-x-1/2 -top-4 px-4 py-1.5  rounded-full bg-white/95 shadow border border-slate-200 text-md font-semibold text-safir-secondary">
                        {t("about.timeline.label", "Services")}
                    </div>
                    <br />
                    <br />

                    <div className="space-y-8 md:space-y-12">
                        {timelineItems.map((item, idx) => {
                            const isLeft = idx % 2 === 0;
                            const columnClass = isLeft
                                ? "md:col-start-1"
                                : "md:col-start-2";
                            const pointerStyle = isLeft
                                ? {
                                      clipPath:
                                          "polygon(0 50%, 100% 0, 100% 100%)",
                                  }
                                : {
                                      clipPath:
                                          "polygon(100% 50%, 0 0, 0 100%)",
                                  };

                            return (
                                <div
                                    key={item.id}
                                    className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center"
                                >
                                    <div className="absolute left-1/2 -translate-x-1/2 top-1 md:top-1 flex items-center justify-center">
                                        <div
                                            className="w-4 h-4 rounded-full border-2 border-white shadow-md"
                                            style={{
                                                backgroundColor: item.color,
                                                boxShadow: `0 0 0 6px ${item.color}22`,
                                            }}
                                        />
                                        <div
                                            className="absolute w-8 h-8 rounded-full"
                                            style={{
                                                boxShadow: `0 0 12px ${item.color}22`,
                                            }}
                                        />
                                    </div>

                                    <motion.div
                                        className={`relative ${columnClass}`}
                                        initial={{
                                            opacity: 0,
                                            y: 36,
                                            scale: 0.98,
                                            rotate: -1,
                                        }}
                                        whileInView={{
                                            opacity: 1,
                                            y: 0,
                                            scale: 1,
                                            rotate: 0,
                                        }}
                                        whileHover={{ y: -6, scale: 1.02 }}
                                        viewport={{ once: true, amount: 0.35 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 160,
                                            damping: 18,
                                            delay: idx * 0.06,
                                        }}
                                    >
                                        <div
                                            className="relative rounded-2xl bg-white/95 backdrop-blur-sm shadow-xl border border-slate-200 p-6 md:p-8 transform transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl"
                                            style={{
                                                borderTop: `5px solid ${item.color}`,
                                            }}
                                        >
                                            <div
                                                className="hidden md:block absolute top-1/2 -translate-y-1/2 w-3 h-3"
                                                style={{
                                                    ...pointerStyle,
                                                    background: item.color,
                                                    [isLeft ? "right" : "left"]:
                                                        "-14px",
                                                }}
                                            />
                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                                {item.label}
                                            </p>
                                            <h3 className="text-xl md:text-2xl font-semibold text-slate-900">
                                                {item.title}
                                            </h3>
                                            <p className="mt-3 text-slate-600 leading-relaxed">
                                                {item.description}
                                            </p>
                                        </div>
                                    </motion.div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HomeAboutPreview;
